import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Grid, Paper, TablePaginationProps } from '@mui/material';

import { Table } from '../../components/table/table';
import { TablePagination } from '../../components';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import { UTCToDateTime } from '../../utils/dates';
import { RouterPaths } from '../../router/paths';
import { useLazyGetDepositInvoicesQuery } from '../../api';
import { formatAmount } from '../../utils';

export const Invoices: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // api
  const [getDepositInvoices, { data: invoices, isLoading: isInvoicesLoading }] =
    useLazyGetDepositInvoicesQuery();

  // pagination logic
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const handleChangePage: TablePaginationProps['onPageChange'] = (e, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage: TablePaginationProps['onRowsPerPageChange'] = (e) => {
    setPage(0);
    setRowsPerPage(parseInt(e.target.value, 10));
  };

  // fetch data according pagination
  useEffect(() => {
    const args = { params: { page: page + 1, page_size: rowsPerPage } };
    getDepositInvoices(args);
  }, [page, rowsPerPage]);

  // table cells and rows
  const headCells: HeadCell[] = [
    {
      id: '1',
      label: t('invoices.head-cells.created-at'),
    },
    {
      id: '2',
      label: t('invoices.head-cells.profile-name'),
    },
    {
      id: '3',
      label: t('invoices.head-cells.amount'),
      align: 'right',
    },
    {
      id: '4',
      label: t('invoices.head-cells.coin'),
    },
    {
      id: '5',
      label: t('invoices.head-cells.payment-method'),
    },
    {
      id: '6',
      label: t('invoices.head-cells.id'),
    },
  ];

  const rows: Row[] | undefined = invoices?.invoices?.map((invoice) => {
    const { id, created_at, profile_name, amount, coin, payment_method, profile_uid } = invoice;

    const cells: RowCell[] = [
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'profile_name',
        value: profile_name,
      },
      {
        name: 'amount',
        value: formatAmount(amount),
        align: 'right',
      },
      {
        name: 'coin',
        value: coin.name,
      },
      {
        name: 'payment_method',
        value: payment_method,
        formatting: (value: string) => {
          return t(`common.payment-method.${value}`);
        },
      },
      {
        name: 'id',
        value: id,
      },
    ];

    return {
      cells,
      onClick: () => navigate(`${RouterPaths.Profiles}/${profile_uid}/details`),
      hover: true,
      sx: { cursor: 'pointer' },
    };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={invoices?.total ?? -1}
            totalPages={invoices?.total_pages ?? 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            isLoading={isInvoicesLoading}
          />
        </Paper>
      </Grid>
    </>
  );
};
